import { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '@/components/layout/Sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Truck, Package, Users, MapPin, Radio, RefreshCw, UserPlus } from 'lucide-react';
import DashboardDateTimeWidget from '@/components/layout/DashboardDateTimeWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'status-warning',
  assigned: 'status-neutral',
  in_transit: 'status-active',
  delivered: 'status-active',
  cancelled: 'status-error',
};

const driverStatusColors = {
  available: 'status-active',
  on_duty: 'bg-primary/20 text-primary border border-primary/30 px-2 py-0.5 rounded text-xs font-bold uppercase',
  off_duty: 'status-neutral',
  on_break: 'status-warning',
};

const CommandCenter = () => {
  const [analytics, setAnalytics] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [unassignedShipments, setUnassignedShipments] = useState([]);
  const [allShipments, setAllShipments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [assignDialog, setAssignDialog] = useState({ open: false, shipment: null });
  const [selectedDriver, setSelectedDriver] = useState('');

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [analyticsRes, driversRes, unassignedRes, shipmentsRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`),
        axios.get(`${API}/users/drivers`),
        axios.get(`${API}/shipments/unassigned/list`),
        axios.get(`${API}/shipments`)
      ]);
      setAnalytics(analyticsRes.data);
      setDrivers(driversRes.data);
      setUnassignedShipments(unassignedRes.data);
      setAllShipments(shipmentsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAssignDriver = async () => {
    if (!selectedDriver || !assignDialog.shipment) return;
    
    try {
      await axios.post(`${API}/shipments/assign`, {
        shipment_id: assignDialog.shipment.id,
        driver_id: selectedDriver
      });
      toast.success('Driver assigned successfully');
      setAssignDialog({ open: false, shipment: null });
      setSelectedDriver('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign driver');
    }
  };

  const availableDrivers = drivers.filter(d => d.driver_status === 'available');

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight">Command Center</h1>
          <p className="text-muted-foreground mt-1">Real-time fleet management and dispatch</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={isLoading} data-testid="refresh-dispatch-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Drivers</p>
                <p className="text-3xl font-heading font-bold">{analytics?.total_drivers || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-emerald-950/50">
                <Truck className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Available</p>
                <p className="text-3xl font-heading font-bold">{analytics?.available_drivers || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-amber-950/50">
                <Package className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Unassigned</p>
                <p className="text-3xl font-heading font-bold">{unassignedShipments.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <Radio className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">In Transit</p>
                <p className="text-3xl font-heading font-bold">{analytics?.in_transit || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map Placeholder */}
        <Card className="lg:col-span-2 card-hover overflow-hidden">
          <CardHeader>
            <CardTitle className="font-heading">Fleet Map</CardTitle>
            <CardDescription>Real-time driver locations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80 bg-muted rounded-lg flex items-center justify-center grid-background">
              <div className="text-center">
                <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Interactive map view</p>
                <p className="text-sm text-muted-foreground/70">Coming soon</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Driver List */}
        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="font-heading">Fleet Status</CardTitle>
            <CardDescription>{drivers.length} total drivers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {drivers.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No drivers registered</p>
              ) : (
                drivers.map((driver) => (
                  <div key={driver.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-card flex items-center justify-center">
                        <Truck className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{driver.name}</p>
                        <p className="text-xs text-muted-foreground">{driver.phone || 'No phone'}</p>
                      </div>
                    </div>
                    <span className={driverStatusColors[driver.driver_status] || 'status-neutral'}>
                      {driver.driver_status?.replace('_', ' ') || 'unknown'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Unassigned Shipments */}
      <Card className="card-hover">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading">Unassigned Shipments</CardTitle>
              <CardDescription>Shipments awaiting driver assignment</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tracking #</TableHead>
                  <TableHead>Origin</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Pickup Date</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unassignedShipments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No unassigned shipments
                    </TableCell>
                  </TableRow>
                ) : (
                  unassignedShipments.map((shipment) => (
                    <TableRow key={shipment.id} data-testid={`unassigned-${shipment.id}`}>
                      <TableCell className="font-mono text-sm">{shipment.tracking_number}</TableCell>
                      <TableCell>{shipment.origin}</TableCell>
                      <TableCell>{shipment.destination}</TableCell>
                      <TableCell>{shipment.cargo_type}</TableCell>
                      <TableCell>{shipment.pickup_date}</TableCell>
                      <TableCell>
                        <Dialog open={assignDialog.open && assignDialog.shipment?.id === shipment.id} onOpenChange={(open) => setAssignDialog({ open, shipment: open ? shipment : null })}>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" className="border-primary/30 text-primary hover:bg-primary/10" data-testid={`assign-btn-${shipment.id}`}>
                              <UserPlus className="w-4 h-4 mr-1" />
                              Assign
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle className="font-heading">Assign Driver</DialogTitle>
                              <DialogDescription>
                                Select a driver for shipment {shipment.tracking_number}
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="p-4 bg-muted rounded-lg">
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div>
                                    <span className="text-muted-foreground">From:</span> {shipment.origin}
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">To:</span> {shipment.destination}
                                  </div>
                                </div>
                              </div>
                              <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                                <SelectTrigger data-testid="driver-select">
                                  <SelectValue placeholder="Select a driver" />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableDrivers.length === 0 ? (
                                    <SelectItem value="none" disabled>No available drivers</SelectItem>
                                  ) : (
                                    availableDrivers.map((driver) => (
                                      <SelectItem key={driver.id} value={driver.id}>
                                        {driver.name}
                                      </SelectItem>
                                    ))
                                  )}
                                </SelectContent>
                              </Select>
                              <Button 
                                className="w-full bg-primary hover:bg-primary/90"
                                onClick={handleAssignDriver}
                                disabled={!selectedDriver}
                                data-testid="confirm-assign-btn"
                              >
                                Assign Driver
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const DriversPage = () => {
  const [drivers, setDrivers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const response = await axios.get(`${API}/users/drivers`);
        setDrivers(response.data);
      } catch (error) {
        toast.error('Failed to fetch drivers');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDrivers();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-heading font-bold tracking-tight">Fleet Management</h1>
      <Card>
        <CardContent className="p-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {drivers.map((driver) => (
                <TableRow key={driver.id}>
                  <TableCell className="font-medium">{driver.name}</TableCell>
                  <TableCell>{driver.email}</TableCell>
                  <TableCell>{driver.phone || '-'}</TableCell>
                  <TableCell>{driver.company || '-'}</TableCell>
                  <TableCell>
                    <span className={driverStatusColors[driver.driver_status] || 'status-neutral'}>
                      {driver.driver_status?.replace('_', ' ') || 'unknown'}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

const ShipmentsPage = () => {
  const [shipments, setShipments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchShipments = async () => {
      try {
        const response = await axios.get(`${API}/shipments`);
        setShipments(response.data);
      } catch (error) {
        toast.error('Failed to fetch shipments');
      } finally {
        setIsLoading(false);
      }
    };
    fetchShipments();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-heading font-bold tracking-tight">All Shipments</h1>
      <Card>
        <CardContent className="p-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tracking #</TableHead>
                <TableHead>Origin</TableHead>
                <TableHead>Destination</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shipments.map((shipment) => (
                <TableRow key={shipment.id}>
                  <TableCell className="font-mono text-sm">{shipment.tracking_number}</TableCell>
                  <TableCell>{shipment.origin}</TableCell>
                  <TableCell>{shipment.destination}</TableCell>
                  <TableCell>{shipment.cargo_type}</TableCell>
                  <TableCell>{shipment.driver_id ? 'Assigned' : 'Unassigned'}</TableCell>
                  <TableCell>
                    <span className={statusColors[shipment.status]}>
                      {shipment.status.replace('_', ' ')}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

const DispatcherDashboard = () => {
  return (
    <div className="min-h-screen bg-background">
      <Sidebar role="dispatcher" />
      <main className="lg:ml-64 p-6 lg:p-10" data-testid="dispatcher-dashboard">
        <div className="flex justify-end mb-4">
          <DashboardDateTimeWidget />
        </div>
        <Routes>
          <Route index element={<CommandCenter />} />
          <Route path="drivers" element={<DriversPage />} />
          <Route path="shipments" element={<ShipmentsPage />} />
        </Routes>
      </main>
    </div>
  );
};

export default DispatcherDashboard;
